import { ChangeEvent, useCallback, useState } from 'react';

import {
  Checkbox,
  Collapse,
  FormHelperText,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { MetricsV1ValueCap } from '@spotify/confidence-graphql';

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'grid',
    columnGap: theme.spacing(2),
    alignItems: 'flex-start',
    gridTemplateColumns: 'max-content 1fr',
  },
  label: {
    cursor: 'pointer',
  },
  capInput: {
    gridRow: 2,
    gridColumn: 2,
  },
  alignedFields: {
    display: 'flex',
    marginTop: theme.spacing(1),
    gap: theme.spacing(2),
  },
  checkboxRoot: {
    padding: 0,
  },
}));

type ValueCapInputProps = {
  value?: MetricsV1ValueCap | null;
  idPrefix: string;
  valueLabel: string;
  onChange: (newValue: MetricsV1ValueCap | null) => void;
  entityName?: string;
  disabled?: boolean;
  short?: boolean;
};

export function ValueCapInput({
  value,
  idPrefix,
  valueLabel,
  onChange,
  entityName,
  disabled,
  short,
}: ValueCapInputProps) {
  const [error, setError] = useState<string | undefined>(undefined);

  const classes = useStyles();
  const useCap = Boolean(value);
  const checkboxId = `${idPrefix}-use-cap-checkbox`;
  const minValue = value?.min?.value ?? '';
  const maxValue = value?.max?.value ?? '';

  const minValid = !isNaN(parseFloat(minValue));
  const maxValid = !isNaN(parseFloat(maxValue));

  const onMinChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newMinStr = e.target.value as string;

      const curMax = parseFloat(maxValue ?? '');
      const newMin = parseFloat(newMinStr);
      if (newMinStr !== '' && newMinStr !== '-' && isNaN(newMin)) {
        setError('Minimum value must be a valid number');
        return;
      }

      onChange({
        min: newMinStr !== '' ? { value: newMinStr } : undefined,
        max: value?.max,
      });
      if (!isNaN(curMax) && !isNaN(newMin) && newMin >= curMax) {
        setError('Minimum value must be smaller than maximum value');
      } else {
        setError(undefined);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxValue, onChange],
  );

  const onMaxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newMaxStr = e.target.value as string;

      const curMin = parseFloat(minValue ?? '');
      const newMax = parseFloat(newMaxStr);
      if (newMaxStr !== '' && newMaxStr !== '-' && isNaN(newMax)) {
        setError('Maximum value must be a valid number');
        return;
      }

      onChange({
        min: value?.min,
        max: newMaxStr !== '' ? { value: newMaxStr } : undefined,
      });
      if (!isNaN(curMin) && !isNaN(newMax) && newMax <= curMin) {
        setError('Maximum value must be larger than minimum value');
      } else {
        setError(undefined);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [minValue, onChange],
  );

  return (
    <div className={classes.wrapper}>
      <Checkbox
        classes={{
          root: classes.checkboxRoot,
        }}
        disabled={disabled}
        checked={useCap}
        name={checkboxId}
        id={checkboxId}
        onChange={e => {
          const isEnabled = e.target.checked;
          setError(undefined);
          onChange(
            isEnabled
              ? {
                  min: { value: '0' },
                  max: { value: '100' },
                }
              : null,
          );
        }}
      />
      <label className={classes.label} htmlFor={checkboxId}>
        <Typography variant="body2" gutterBottom>
          Cap the {valueLabel} after aggregation on {entityName || 'entity'}
          -level.
        </Typography>
        <FormHelperText>
          Setting a minimum or maximum value will ensure that no value (after
          aggregation) for a single entity will go outside of those bounds.
          {short ? (
            ''
          ) : (
            <>
              {' '}
              This is useful to prevent outliers that would impact the metric
              unexpectedly. For example, a user cannot consume more than 24
              hours worth of music in a single day.
            </>
          )}
        </FormHelperText>
      </label>
      <Collapse in={useCap} unmountOnExit className={classes.capInput}>
        <div className={classes.alignedFields}>
          <TextField
            variant="outlined"
            margin="dense"
            label="Minimum value"
            name={`${idPrefix}-min-field`}
            value={minValue}
            onChange={onMinChange}
            type="number"
            disabled={disabled}
            error={!!error}
          />
          <TextField
            variant="outlined"
            margin="dense"
            label="Maximum value"
            name={`${idPrefix}-max-field`}
            value={maxValue}
            onChange={onMaxChange}
            type="number"
            disabled={disabled}
            error={!!error}
          />
        </div>
        {error && (
          <FormHelperText error={Boolean(error)}>{error}</FormHelperText>
        )}
        <FormHelperText>
          Aggregated values within {entityName || 'entity'}
          {!minValue && !maxValue ? ' are not capped' : ''}
          {minValid
            ? ` smaller than ${minValue} are capped to ${minValue}`
            : ''}
          {minValid && maxValid ? ', and values' : ''}
          {maxValid ? ` larger than ${maxValue} are capped to ${maxValue}` : ''}
          .
        </FormHelperText>
      </Collapse>
    </div>
  );
}
